import { BlockComponent } from '@blocksuite/affine/block-std';
import { html, nothing, svg, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';

import { AIProvider } from '../../provider/ai-provider';
import type { AnalyzeResultBlockModel } from './model';
import { analyzeResultStyles } from './styles';

/**
 * Capsule artifact shape — duplicated from the backend's Zod schema
 * (`packages/backend/server/src/plugins/copilot/orchestration/types/analyze.ts`)
 * because we don't import backend types into the frontend bundle. If
 * the backend schema changes, update both.
 */
interface CapsuleCell {
  cellType: 'code' | 'markdown';
  source: string;
  metadata?: Record<string, unknown>;
}

interface CapsuleArtifact {
  cells: CapsuleCell[];
  seed: number | null;
  packageLock: Record<string, string | undefined>;
  inputFile: {
    name: string;
    sha256: string;
    rowCount: number;
    columnCount: number;
  };
  createdAt: string;
  generator: string;
}

/**
 * Loose AnalyzeResult shape — only the fields the block actually
 * reads. We deliberately don't import the Zod schema; the JSON has
 * already been parsed before it lands in the block's `analyzeResultJson`.
 */
interface AnalyzeResultLoose {
  approach?: string;
  capsule?: CapsuleArtifact;
  methodsParagraph?: string;
  methodsParagraphStyle?: string;
  /**
   * Pre-test assumption gate (normality + variance) for a parametric
   * comparison — rendered as an OK / WARN / FAIL badge with the recommended
   * test and the nonparametric fallback's p-value.
   */
  assumptions?: {
    verdict?: 'ok' | 'warn' | 'fail';
    recommendation?: string;
    normality?: { tested?: boolean; allNormal?: boolean; minPValue?: number };
    equalVariance?: {
      tested?: boolean;
      equal?: boolean;
      pValue?: number;
    } | null;
    nonparametric?: { test?: string; pValue?: number };
  };
  /**
   * Per-statistic results (IC50, Hill slope, Pearson r, t-test p, …) rendered
   * as a structured card: value / 95% CI / p / q(FDR) / n, with significance
   * styling and click-to-copy cells. Previously these surfaced only as a
   * markdown table inserted beneath the card. `q` (FDR-adjusted) is
   * render-if-present — there is no backend field for it yet.
   */
  statistics?: Array<{
    name: string;
    value: number;
    unit?: string;
    ci95?: [number, number];
    pValue?: number;
    n?: number;
    q?: number;
  }>;
  /**
   * The actual analysis plots produced by the backend (fitted dose-response
   * curve, volcano, etc.). Previously computed then discarded at render —
   * the block only drew a scatter off the 10-row preview. Now rendered.
   */
  plots?: Array<{
    kind?: string;
    title?: string;
    xLabel?: string;
    yLabel?: string;
    data?: {
      x?: number[];
      y?: number[];
      yFitted?: number[];
      curveX?: number[];
      significant?: boolean[];
      gene?: string[];
      labels?: string[];
      values?: number[];
      /** 1-based original row number per point → click-to-exclude. */
      rowIndex?: number[];
    };
  }>;
}

interface DatasetProfileLoose {
  name?: string;
  rowCount?: number;
  columnCount?: number;
  kind?: string;
  kindConfidence?: number;
  preview?: Array<Record<string, unknown>>;
  columns?: Array<{
    name: string;
    dtype: string;
    nullCount: number;
    stats?: NumericColumnStats;
  }>;
}

interface NumericColumnStats {
  min?: number;
  max?: number;
  mean?: number;
  median?: number;
  std?: number;
  outlierCount?: number;
}

/**
 * Analyze-result block — Phase E custom BlockSuite block.
 *
 * Renders a thin compact card on top of the existing markdown
 * rendering (which still appears below as separate blocks). The
 * card's job is to surface the *interactive* affordances that
 * markdown can't carry:
 *
 * - **Download .ipynb** button (the headline win — no more
 *   copy-pasting JSON out of a code block)
 * - **Copy capsule SHA-256** for citation in a paper
 * - At-a-glance metadata (kind + confidence + row/col counts +
 *   methods style + seed)
 *
 * Charts, statistics tables, and the methods paragraph stay in the
 * markdown blocks beneath this card. Click-to-exclude on chart
 * points is a follow-up — needs Recharts integration that's a
 * separate scope from this block.
 */
export class AnalyzeResultBlockComponent extends BlockComponent<AnalyzeResultBlockModel> {
  static override styles = analyzeResultStyles;

  @state() private accessor toast = '';

  /**
   * BlockComponent renders into light DOM (no shadow root), so the
   * `:host { display: block; margin: ... }` rule from `static styles`
   * never applies — the custom element stays `display: inline` and
   * vertical margin doesn't push neighbouring blocks. Set the host
   * sizing + margin imperatively so the card has proper breathing
   * room. Same pattern is applied to agent-stages and deep-research-
   * result blocks for consistency.
   */
  override connectedCallback(): void {
    super.connectedCallback();
    this.style.display = 'block';
    this.style.margin = '24px 0 16px';
  }

  /**
   * Parse the JSON-string props into typed objects. Defensive: bad
   * JSON returns `null` and the render falls through to a minimal
   * placeholder rather than crashing the doc.
   */
  private get parsedResult(): AnalyzeResultLoose | null {
    const raw = this.model.props.analyzeResultJson;
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AnalyzeResultLoose;
    } catch {
      return null;
    }
  }

  private get parsedProfile(): DatasetProfileLoose | null {
    const raw = this.model.props.datasetProfileJson;
    if (!raw) return null;
    try {
      return JSON.parse(raw) as DatasetProfileLoose;
    } catch {
      return null;
    }
  }

  private showToast(msg: string): void {
    this.toast = msg;
    setTimeout(() => {
      if (this.toast === msg) this.toast = '';
    }, 1800);
  }

  /**
   * Convert a CapsuleArtifact into a valid Jupyter .ipynb v4 JSON
   * document, then trigger a browser download. Mirrors the renderer's
   * `capsuleToIpynb` helper but emits a real File via Blob → anchor
   * click → URL.createObjectURL.
   */
  private downloadIpynb = (): void => {
    const result = this.parsedResult;
    if (!result?.capsule) {
      this.showToast('No capsule on this analysis');
      return;
    }
    const cap = result.capsule;
    const cells = cap.cells.map(c => ({
      cell_type: c.cellType,
      metadata: c.metadata ?? {},
      source: c.source
        .split('\n')
        .map((line, i, arr) => (i === arr.length - 1 ? line : line + '\n')),
      ...(c.cellType === 'code' ? { execution_count: null, outputs: [] } : {}),
    }));
    const pythonVersion = cap.packageLock.python ?? '3.11';
    const ipynb = {
      cells,
      metadata: {
        kernelspec: {
          display_name: `Python ${pythonVersion}`,
          language: 'python',
          name: `python${pythonVersion.split('.')[0]}`,
        },
        language_info: {
          name: 'python',
          version: pythonVersion,
        },
        curiescious: {
          seed: cap.seed,
          packageLock: cap.packageLock,
          inputFile: cap.inputFile,
          createdAt: cap.createdAt,
          generator: cap.generator,
        },
      },
      nbformat: 4,
      nbformat_minor: 5,
    };
    const blob = new Blob([JSON.stringify(ipynb, null, 2)], {
      type: 'application/x-ipynb+json',
    });
    const a = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const baseName = (cap.inputFile.name ?? 'analysis').replace(/\.[^.]+$/, '');
    a.href = url;
    a.download = `${baseName}.curiescious.ipynb`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    this.showToast('Notebook downloaded');
  };

  private copySha256 = async (): Promise<void> => {
    const sha = this.parsedResult?.capsule?.inputFile.sha256;
    if (!sha) {
      this.showToast('No SHA-256 to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(sha);
      this.showToast('SHA-256 copied');
    } catch {
      this.showToast('Clipboard blocked');
    }
  };

  /**
   * Render the pre-test assumption gate as an OK / WARN / FAIL badge with the
   * recommendation and (when the parametric assumptions fail) the
   * nonparametric fallback's p-value. This is the transparent-assumptions
   * "moat" surfaced in the UI.
   */
  private renderAssumptions(
    a: NonNullable<AnalyzeResultLoose['assumptions']>
  ): TemplateResult {
    const verdict = a.verdict ?? 'ok';
    const map = {
      ok: ['#1e7a3c', 'rgba(42,138,74,0.10)', 'OK'],
      warn: ['#a06a00', 'rgba(214,153,0,0.12)', 'WARN'],
      fail: ['#b3261e', 'rgba(210,59,59,0.10)', 'FAIL'],
    } as const;
    const [fg, bg, label] = map[verdict];
    const parts: string[] = [];
    if (a.normality?.tested)
      parts.push(
        `normality ${a.normality.allNormal ? 'OK' : 'violated'} (p=${(a.normality.minPValue ?? 0).toFixed(3)})`
      );
    if (a.equalVariance?.tested)
      parts.push(
        `equal-variance ${a.equalVariance.equal ? 'OK' : 'violated'} (Levene p=${(a.equalVariance.pValue ?? 0).toFixed(3)})`
      );
    return html`<div
      style=${`margin:10px 0;padding:8px 12px;border-radius:8px;background:${bg};border-left:3px solid ${fg};font-size:13px`}
    >
      <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap">
        <strong style=${`color:${fg}`}>Assumptions: ${label}</strong>
        ${parts.length
          ? html`<span style="color:#666">${parts.join(' · ')}</span>`
          : nothing}
      </div>
      ${a.recommendation
        ? html`<div style="margin-top:4px;color:#333">${a.recommendation}</div>`
        : nothing}
      ${a.nonparametric?.test
        ? html`<div style="margin-top:2px;color:#333">
            Nonparametric ${a.nonparametric.test}: p =
            ${(a.nonparametric.pValue ?? 1).toExponential(2)}
          </div>`
        : nothing}
    </div>`;
  }

  /** Copy text to the clipboard with a toast (reuses the copySha256 pattern). */
  private copyText = async (text: string, okMsg = 'Copied'): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      this.showToast(okMsg);
    } catch {
      this.showToast('Clipboard blocked');
    }
  };

  /**
   * Structured statistics card (M7). Renders one row per statistic with
   * value / 95% CI / p / q(FDR) / n, significance styling, and click-to-copy
   * cells (plus a "copy table" TSV button for pasting into a manuscript or
   * Excel). Replaces the markdown `### Statistics` table the doc-writer used
   * to insert beneath the card. The q (FDR) column appears only when a stat
   * carries a q-value — there is no backend field for it yet, so it is
   * render-if-present. Significance is keyed to q when present (the honest
   * threshold for multiple comparisons), otherwise to the raw p-value.
   */
  private renderStatistics(
    stats: NonNullable<AnalyzeResultLoose['statistics']>
  ): TemplateResult {
    const hasQ = stats.some(s => s.q != null && Number.isFinite(s.q));
    const sigOf = (s: { pValue?: number; q?: number }): number | undefined => {
      const v = hasQ && s.q != null ? s.q : s.pValue;
      return v != null && Number.isFinite(v) ? v : undefined;
    };
    const stars = (p?: number): string =>
      p == null
        ? ''
        : p < 0.001
          ? '***'
          : p < 0.01
            ? '**'
            : p < 0.05
              ? '*'
              : '';
    const sigColor = (p?: number): string =>
      p == null ? '#999' : p < 0.05 ? '#1e7a3c' : '#666';
    const fmtP = (p?: number): string =>
      p == null || !Number.isFinite(p)
        ? '—'
        : p < 0.001
          ? p.toExponential(2)
          : p.toFixed(3);
    const fmtCi = (ci?: [number, number]): string =>
      ci ? `${fmtNum(ci[0])} – ${fmtNum(ci[1])}` : '—';
    const fmtVal = (s: { value: number; unit?: string }): string =>
      `${fmtNum(s.value)}${s.unit ? ` ${s.unit}` : ''}`;

    const cellStyle = (color?: string, bold?: boolean): string =>
      `padding:4px 10px;border-bottom:1px solid var(--affine-border-color,#eee);cursor:pointer;white-space:nowrap;${
        color ? `color:${color};` : ''
      }${bold ? 'font-weight:600;' : ''}`;
    const cell = (
      display: string | TemplateResult,
      copy: string,
      opts?: { color?: string; bold?: boolean }
    ): TemplateResult =>
      html`<td
        style=${cellStyle(opts?.color, opts?.bold)}
        title="Click to copy"
        @click=${() => this.copyText(copy)}
      >
        ${display}
      </td>`;
    const th = (text: string): TemplateResult =>
      html`<th
        style="padding:4px 10px;text-align:left;font-weight:600;color:var(--affine-text-secondary-color,#666);border-bottom:1px solid var(--affine-border-color,#ddd);white-space:nowrap"
      >
        ${text}
      </th>`;

    const tsv = [
      ['Statistic', 'Value', '95% CI', 'p', ...(hasQ ? ['q (FDR)'] : []), 'n'].join(
        '\t'
      ),
      ...stats.map(s =>
        [
          s.name,
          fmtVal(s),
          fmtCi(s.ci95),
          fmtP(s.pValue),
          ...(hasQ ? [fmtP(s.q)] : []),
          s.n != null ? String(s.n) : '—',
        ].join('\t')
      ),
    ].join('\n');

    return html`<div style="margin:10px 0">
      <div
        style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px"
      >
        <strong style="font-size:13px">Statistics</strong>
        <button
          style="font-size:11px;padding:2px 8px;border:1px solid var(--affine-border-color,#ddd);border-radius:6px;background:transparent;cursor:pointer;color:var(--affine-text-secondary-color,#666)"
          title="Copy the whole table (TSV — paste into Excel or a manuscript)"
          @click=${() => this.copyText(tsv, 'Statistics table copied')}
        >
          ⧉ Copy table
        </button>
      </div>
      <table
        style="border-collapse:collapse;font-size:13px;width:100%;font-variant-numeric:tabular-nums"
      >
        <thead>
          <tr>
            ${th('Statistic')} ${th('Value')} ${th('95% CI')} ${th('p')}
            ${hasQ ? th('q (FDR)') : nothing} ${th('n')}
          </tr>
        </thead>
        <tbody>
          ${stats.map(s => {
            const sv = sigOf(s);
            const star = stars(sv);
            const col = sigColor(sv);
            const rowBg =
              sv != null && sv < 0.05 ? 'background:rgba(42,138,74,0.06)' : '';
            return html`<tr style=${rowBg}>
              ${cell(s.name, s.name, { bold: true })}
              ${cell(fmtVal(s), fmtVal(s))} ${cell(fmtCi(s.ci95), fmtCi(s.ci95))}
              ${cell(
                html`${fmtP(s.pValue)}${star
                  ? html`<span style="margin-left:3px">${star}</span>`
                  : nothing}`,
                fmtP(s.pValue),
                { color: col, bold: !!star }
              )}
              ${hasQ ? cell(fmtP(s.q), fmtP(s.q), { color: col }) : nothing}
              ${cell(
                s.n != null ? String(s.n) : '—',
                s.n != null ? String(s.n) : '—'
              )}
            </tr>`;
          })}
        </tbody>
      </table>
      <div style="margin-top:4px;font-size:11px;color:#999">
        * p&lt;0.05 · ** p&lt;0.01 · *** p&lt;0.001${hasQ
          ? ' · significance keyed to q (FDR-adjusted)'
          : ''}
      </div>
    </div>`;
  }

  private renderMeta(
    result: AnalyzeResultLoose,
    profile: DatasetProfileLoose | null
  ): TemplateResult {
    const cap = result.capsule;
    const items: Array<[string, string]> = [];
    if (profile?.name) items.push(['Dataset', profile.name]);
    if (profile?.rowCount !== undefined && profile?.columnCount !== undefined) {
      items.push(['Shape', `${profile.rowCount} × ${profile.columnCount}`]);
    }
    if (cap?.seed !== null && cap?.seed !== undefined) {
      items.push(['Seed', String(cap.seed)]);
    }
    if (cap?.inputFile.sha256) {
      items.push(['SHA', cap.inputFile.sha256.slice(0, 8)]);
    }
    if (result.methodsParagraphStyle) {
      items.push(['Methods', result.methodsParagraphStyle]);
    }
    if (items.length === 0) return html`${nothing}`;
    return html`<div class="meta">
      ${items.map(
        ([k, v]) =>
          html`<span class="meta-item"
            ><span class="label">${k}</span
            ><span class="value">${v}</span></span
          >`
      )}
    </div>`;
  }

  /**
   * HTS plate grid renderer (Phase E #72).
   *
   * Reads the dataset profile's preview rows when kind=hts-plate and
   * renders a 96-well-style grid (or whatever shape the data implies).
   * Wells are coloured by signal value; hits (>3σ above DMSO mean) get
   * a red outline. Z′-factor is computed inline from the DMSO group.
   *
   * Pure-render — no network calls. Only works on the preview slice
   * we already have; the user gets a visual cue across whatever rows
   * the parser surfaced (10 rows = ~10 wells; for full-plate analysis
   * the backend tools still run on all rows).
   */
  private renderPlateGrid(profile: DatasetProfileLoose): TemplateResult | null {
    if (profile.kind !== 'hts-plate') return null;
    const rows = profile.preview ?? [];
    if (rows.length === 0) return null;

    const cols = profile.columns ?? [];
    const findCol = (re: RegExp): string | undefined =>
      cols.find(c => re.test(c.name))?.name;

    const wellNameCol = findCol(/^(well|well_?id|position)/i);
    const rowCol = findCol(/^(row|plate_?row)/i);
    const colCol = findCol(/^(col|column|plate_?col)/i);
    const signalCol = findCol(
      /^(raw|signal|norm|normalized|measurement|value)/i
    );
    const compoundCol = findCol(/^(compound|treatment|drug|sample_?id)/i);
    if (!signalCol) return null;

    type Coord = { rowIdx: number; colIdx: number };
    const parseWellId = (wid: string): Coord | null => {
      const match = /^([A-P])(\d{1,2})$/i.exec(wid.trim());
      if (!match) return null;
      const r = match[1].toUpperCase().charCodeAt(0) - 'A'.charCodeAt(0);
      const c = parseInt(match[2], 10) - 1;
      return { rowIdx: r, colIdx: c };
    };

    type WellEntry = {
      coord: Coord;
      signal: number;
      compound: string;
      isControl: boolean;
    };
    const wells: WellEntry[] = [];
    for (const r of rows) {
      let coord: Coord | null = null;
      if (rowCol && colCol) {
        const rowVal = String(r[rowCol] ?? '').trim();
        const colVal = Number(r[colCol]);
        if (rowVal && Number.isFinite(colVal)) {
          coord = {
            rowIdx: rowVal.toUpperCase().charCodeAt(0) - 'A'.charCodeAt(0),
            colIdx: colVal - 1,
          };
        }
      } else if (wellNameCol) {
        coord = parseWellId(String(r[wellNameCol] ?? ''));
      }
      if (!coord) continue;
      const signal = Number(r[signalCol]);
      if (!Number.isFinite(signal)) continue;
      const compound = compoundCol ? String(r[compoundCol] ?? '').trim() : '';
      wells.push({
        coord,
        signal,
        compound,
        isControl: /^(dmso|negative|neg|ctrl|control)$/i.test(compound),
      });
    }
    if (wells.length === 0) return null;

    const rowMax = Math.max(...wells.map(w => w.coord.rowIdx), 0);
    const colMax = Math.max(...wells.map(w => w.coord.colIdx), 0);
    // Snap to standard plate sizes when close (8×12=96, 16×24=384)
    const numRows = rowMax >= 8 ? 16 : 8;
    const numCols = colMax >= 12 ? 24 : 12;

    const controls = wells.filter(w => w.isControl).map(w => w.signal);
    const samples = wells.filter(w => !w.isControl).map(w => w.signal);
    const meanCtrl =
      controls.length > 0
        ? controls.reduce((s, v) => s + v, 0) / controls.length
        : 0;
    const stdCtrl = (() => {
      if (controls.length < 2) return 0;
      const variance =
        controls.reduce((s, v) => s + (v - meanCtrl) ** 2, 0) / controls.length;
      return Math.sqrt(variance);
    })();

    const meanSamp =
      samples.length > 0
        ? samples.reduce((s, v) => s + v, 0) / samples.length
        : 0;
    const stdSamp = (() => {
      if (samples.length < 2) return 0;
      const variance =
        samples.reduce((s, v) => s + (v - meanSamp) ** 2, 0) / samples.length;
      return Math.sqrt(variance);
    })();

    // Z′-factor: 1 - 3*(σpos + σneg) / |μpos − μneg|
    const zPrime =
      Math.abs(meanSamp - meanCtrl) > 0
        ? 1 - (3 * (stdSamp + stdCtrl)) / Math.abs(meanSamp - meanCtrl)
        : null;

    const hitThreshold = meanCtrl + 3 * stdCtrl;
    const sigMin = Math.min(...wells.map(w => w.signal));
    const sigMax = Math.max(...wells.map(w => w.signal));
    const range = sigMax - sigMin || 1;

    const wellMap = new Map<string, WellEntry>();
    for (const w of wells) {
      wellMap.set(`${w.coord.rowIdx}:${w.coord.colIdx}`, w);
    }

    const cells: TemplateResult[] = [];
    cells.push(html`<div class="plate-corner"></div>`);
    for (let c = 0; c < numCols; c++) {
      cells.push(html`<div class="plate-col-header">${c + 1}</div>`);
    }
    for (let r = 0; r < numRows; r++) {
      cells.push(
        html`<div class="plate-row-header">${String.fromCharCode(65 + r)}</div>`
      );
      for (let c = 0; c < numCols; c++) {
        const w = wellMap.get(`${r}:${c}`);
        if (!w) {
          cells.push(html`<div class="well" data-empty="true"></div>`);
          continue;
        }
        const t = (w.signal - sigMin) / range;
        // Blue gradient — light → dark by signal magnitude
        const lightness = Math.round(85 - t * 50);
        const bg = `hsl(217, 90%, ${lightness}%)`;
        const isHit = !w.isControl && stdCtrl > 0 && w.signal > hitThreshold;
        cells.push(
          html`<div
            class="well"
            data-hit=${isHit ? 'true' : 'false'}
            data-control=${w.isControl ? 'true' : 'false'}
            style="background:${bg}"
            title="${`${String.fromCharCode(65 + r)}${(c + 1).toString().padStart(2, '0')} • signal=${w.signal.toFixed(2)}${w.compound ? ` • ${w.compound}` : ''}${isHit ? ' • HIT' : ''}${w.isControl ? ' • control' : ''}`}"
          ></div>`
        );
      }
    }

    const hits = wells.filter(
      w => !w.isControl && stdCtrl > 0 && w.signal > hitThreshold
    ).length;

    return html`<div class="plate">
      <div class="plate-title">
        ${numRows}×${numCols} plate · ${wells.length} wells in preview
      </div>
      <div
        class="plate-grid"
        style="grid-template-columns: 18px repeat(${numCols}, 16px);"
      >
        ${cells}
      </div>
      <div class="plate-summary">
        <span
          ><strong>Z′-factor:</strong> ${zPrime !== null
            ? zPrime.toFixed(2)
            : 'n/a'}</span
        >
        <span
          ><strong>Controls:</strong> ${controls.length}
          (μ=${meanCtrl.toFixed(0)}, σ=${stdCtrl.toFixed(0)})</span
        >
        <span><strong>Hits (>3σ):</strong> ${hits}</span>
      </div>
      <div class="plate-legend">
        <span>low</span>
        <span class="plate-legend-bar"></span>
        <span>high signal</span>
      </div>
    </div>`;
  }

  override renderBlock(): TemplateResult {
    const result = this.parsedResult;
    const profile = this.parsedProfile;
    if (!result && !profile) {
      return html`<div class="card">
        <div class="body">
          <em>(No analyze result on this block.)</em>
        </div>
      </div>`;
    }
    const hasCapsule = !!result?.capsule;
    const titleLabel = result ? 'Analysis result' : 'Dataset overview';
    // Surface the detected dataset kind as a pill badge — same
    // visual idiom as agent-stages' Analyze/Explore mode badge so
    // the two cards read as a designed pair.
    const kindBadge = profile?.kind
      ? html`<span class="kind-badge"
          >${profile.kind}${profile.kindConfidence !== undefined
            ? ` · ${Math.round(profile.kindConfidence * 100)}%`
            : ''}</span
        >`
      : nothing;
    return html`<div class="card">
      <div class="header">
        <div class="title-row">
          <div class="title">${titleLabel}</div>
          ${kindBadge}
        </div>
        <div class="actions">
          <button
            class="btn"
            data-disabled=${hasCapsule ? 'false' : 'true'}
            ?disabled=${!hasCapsule}
            @click=${this.downloadIpynb}
            title="Download a Jupyter notebook (.ipynb) reproducing this analysis"
          >
            ⇩ Download .ipynb
          </button>
          <button
            class="btn"
            data-disabled=${hasCapsule ? 'false' : 'true'}
            ?disabled=${!hasCapsule}
            @click=${this.copySha256}
            title="Copy the SHA-256 of the input file for citation"
          >
            ⧉ Copy hash
          </button>
        </div>
      </div>
      ${result
        ? this.renderMeta(result, profile)
        : this.renderProfileMeta(profile)}
      ${result?.assumptions
        ? this.renderAssumptions(result.assumptions)
        : nothing}
      ${result?.statistics && result.statistics.length > 0
        ? this.renderStatistics(result.statistics)
        : nothing}
      ${profile && profile.kind === 'hts-plate'
        ? this.renderPlateGrid(profile)
        : profile
          ? this.renderCsvPreview(profile)
          : nothing}
      ${result?.plots && result.plots.length > 0
        ? this.renderResultPlots(result.plots)
        : profile && profile.kind !== 'hts-plate'
          ? this.renderPlots(profile)
          : nothing}
      ${this.toast ? html`<div class="toast">${this.toast}</div>` : nothing}
    </div>`;
  }

  /**
   * Render a first-10-rows preview table for tabular datasets that
   * aren't HTS plates. Researchers need to see what they uploaded
   * before asking the AI to analyse it — without this, the only
   * visible signal is the dataset-kind pill, which is too thin to
   * catch a mis-uploaded file (wrong delimiter, off-by-one header,
   * etc.).
   *
   * Renders:
   *  - column headers with dtype badges
   *  - up to 10 rows (whatever the backend put in `preview`)
   *  - numeric-column stat chips (min · median · mean · max · σ),
   *    plus an outlier count when the backend flagged any.
   */
  private renderCsvPreview(
    profile: DatasetProfileLoose
  ): TemplateResult | typeof nothing {
    const cols = profile.columns ?? [];
    const rows = profile.preview ?? [];
    if (cols.length === 0 || rows.length === 0) return nothing;

    const totalRows = profile.rowCount;
    const previewCount = rows.length;
    const rowHint =
      totalRows && totalRows > previewCount
        ? `Showing first ${previewCount} of ${totalRows.toLocaleString()} rows`
        : `Showing ${previewCount} row${previewCount === 1 ? '' : 's'}`;

    const numericCols = cols.filter(
      c => c.stats && Number.isFinite(c.stats.mean)
    );

    return html`<div class="csv-preview">
      <div class="csv-preview-title">Data preview</div>
      <div class="csv-preview-wrap">
        <table class="csv-preview-table">
          <thead>
            <tr>
              ${cols.map(
                c =>
                  html`<th title=${`${c.dtype} · ${c.nullCount} nulls`}>
                    ${c.name}
                    <span
                      class="csv-dtype"
                      data-numeric=${isNumericDtype(c.dtype) ? 'true' : 'false'}
                      data-bool=${isBoolDtype(c.dtype) ? 'true' : 'false'}
                      data-date=${isDateDtype(c.dtype) ? 'true' : 'false'}
                      >${shortDtype(c.dtype)}</span
                    >
                  </th>`
              )}
            </tr>
          </thead>
          <tbody>
            ${rows.map(
              row =>
                html`<tr>
                  ${cols.map(c => {
                    const v = row[c.name];
                    if (v === null || v === undefined || v === '') {
                      return html`<td class="csv-null">—</td>`;
                    }
                    return html`<td title=${String(v)}>${formatCell(v)}</td>`;
                  })}
                </tr>`
            )}
          </tbody>
        </table>
      </div>
      <div class="csv-preview-rowhint">${rowHint}</div>
      ${numericCols.length > 0
        ? html`<div class="csv-stats">
            ${numericCols.map(c => {
              const s = c.stats!;
              const parts: TemplateResult[] = [];
              parts.push(
                html`<strong>${c.name}</strong><span class="sep">·</span>`
              );
              if (Number.isFinite(s.min)) {
                parts.push(html`min ${fmtNum(s.min!)}`);
                parts.push(html`<span class="sep">·</span>`);
              }
              if (Number.isFinite(s.median)) {
                parts.push(html`med ${fmtNum(s.median!)}`);
                parts.push(html`<span class="sep">·</span>`);
              }
              if (Number.isFinite(s.mean)) {
                parts.push(html`μ ${fmtNum(s.mean!)}`);
                parts.push(html`<span class="sep">·</span>`);
              }
              if (Number.isFinite(s.max)) {
                parts.push(html`max ${fmtNum(s.max!)}`);
              }
              if (Number.isFinite(s.std) && s.std! > 0) {
                parts.push(html`<span class="sep">·</span>σ ${fmtNum(s.std!)}`);
              }
              if (s.outlierCount && s.outlierCount > 0) {
                parts.push(
                  html`<span class="sep">·</span>⚠ ${s.outlierCount}
                    outlier${s.outlierCount === 1 ? '' : 's'}`
                );
              }
              return html`<span class="csv-stat-chip">${parts}</span>`;
            })}
          </div>`
        : nothing}
    </div>`;
  }

  // (helpers for renderCsvPreview live at module scope below — see
  // shortDtype / isNumericDtype / fmtNum / formatCell.)

  /**
   * Render up to two SVG scatter plots from the preview rows so the
   * researcher gets an immediate visual on the dataset. Axis choice
   * is heuristic: dose/concentration → X, response/effect → Y, with
   * a "first two numeric columns" fallback for generic datasets.
   * SVG is generated client-side (no Recharts/Plotly dependency) and
   * each plot ships PNG/SVG download buttons.
   */
  private renderPlots(
    profile: DatasetProfileLoose
  ): TemplateResult | typeof nothing {
    const rows = profile.preview ?? [];
    const cols = profile.columns ?? [];
    if (rows.length < 2 || cols.length === 0) return nothing;

    const numericCols = cols.filter(c => isNumericDtype(c.dtype));
    if (numericCols.length < 2) return nothing;

    const doseRe = /(dose|conc|concentration|treat|drug|x_?val)/i;
    const respRe = /(response|inhib|activ|effect|signal|y_?val|expr|fold)/i;
    const doseCol = numericCols.find(c => doseRe.test(c.name));
    const respCol = numericCols.find(c => respRe.test(c.name));

    const pairs: Array<[string, string]> = [];
    if (doseCol && respCol && doseCol.name !== respCol.name) {
      pairs.push([doseCol.name, respCol.name]);
    } else {
      pairs.push([numericCols[0].name, numericCols[1].name]);
    }
    if (numericCols.length >= 3) {
      const [xUsed, yUsed] = pairs[0];
      const extra = numericCols.find(c => c.name !== xUsed && c.name !== yUsed);
      if (extra) pairs.push([numericCols[0].name, extra.name]);
    }

    return html`<div class="plots">
      ${pairs.map(([xName, yName]) => this.renderScatter(rows, xName, yName))}
    </div>`;
  }

  /**
   * Render the backend's ACTUAL analysis plots (`AnalyzeResult.plots`) — the
   * fitted dose-response curve, volcano, etc. — via the same SVG engine,
   * overlaying the model's fitted curve (curveX/yFitted) on the raw scatter.
   * Previously these were computed server-side then discarded; the block only
   * drew a scatter off the 10-row preview.
   */
  /**
   * Click-to-exclude: ask the chat to re-run the analysis without the clicked
   * row. The handler's session cache + exclusion parser ("exclude row N")
   * refits on the cached dataset minus that row through the streaming
   * pipeline — no re-upload needed.
   */
  private excludePointAndRefit(rowNumber: number): void {
    AIProvider.slots.requestRunQuery.next({
      query: `Refit excluding row ${rowNumber}`,
    });
    this.showToast(`Refitting without row ${rowNumber}…`);
  }

  private renderResultPlots(
    plots: NonNullable<AnalyzeResultLoose['plots']>
  ): TemplateResult | typeof nothing {
    const cards = plots
      .map(plot => {
        const kind = plot.kind ?? 'scatter';

        // Bar chart (e.g. HTS control means).
        if (kind === 'bar' && plot.data?.labels && plot.data?.values) {
          return this.renderBar(
            plot.data.labels,
            plot.data.values,
            plot.title ?? 'Bar chart',
            plot.yLabel ?? ''
          );
        }

        const x = plot.data?.x ?? [];
        const y = plot.data?.y ?? [];
        if (x.length < 2 || y.length < 2) return null;
        const xName = plot.xLabel ?? 'x';
        const yName = plot.yLabel ?? 'y';
        const rows = x.map((xv, i) => ({ [xName]: xv, [yName]: y[i] }));
        const genes = plot.data?.gene;
        const pointTitles = x.map((xv, i) =>
          genes?.[i] ? `${genes[i]} (${xv}, ${y[i]})` : `${xv}, ${y[i]}`
        );

        // Kaplan-Meier — render as a descending step function.
        if (kind === 'kaplan-meier') {
          return this.renderScatter(rows, xName, yName, null, false, {
            titleOverride: plot.title,
            lineMode: 'step',
            pointTitles,
          });
        }

        // Volcano — colour points by significance/direction + threshold guides.
        if (kind === 'volcano') {
          const sig = plot.data?.significant ?? [];
          const colors = x.map((xv, i) =>
            !sig[i] ? '#9aa0a6' : Number(xv) > 0 ? '#d23b3b' : '#2a8a4a'
          );
          return this.renderScatter(rows, xName, yName, null, false, {
            titleOverride: plot.title,
            colors,
            pointTitles,
            vLines: [-1, 1],
            hLine: -Math.log10(0.05),
          });
        }

        // Scatter / line (dose-response etc.) — fitted curve + log-x.
        const curveX = plot.data?.curveX ?? [];
        const yFitted = plot.data?.yFitted ?? [];
        const fit =
          curveX.length > 1 && yFitted.length === curveX.length
            ? { x: curveX, y: yFitted }
            : null;
        const allPositive = x.every(v => v > 0);
        const ratio = allPositive ? Math.max(...x) / Math.min(...x) : 1;
        const logX = allPositive && ratio >= 100;
        return this.renderScatter(rows, xName, yName, fit, logX, {
          titleOverride: plot.title,
          pointTitles,
          rowIndices: plot.data?.rowIndex,
        });
      })
      .filter((c): c is TemplateResult => c !== null);
    if (cards.length === 0) return nothing;
    return html`<div class="plots">${cards}</div>`;
  }

  /** Decade ticks (…, 0.01, 0.1, 1, 10, …) within [min, max] for a log axis. */
  private makeLogTicks(min: number, max: number): number[] {
    if (!(min > 0) || !(max > 0)) return [min, max];
    const ticks: number[] = [];
    const lo = Math.floor(Math.log10(min));
    const hi = Math.ceil(Math.log10(max));
    for (let e = lo; e <= hi; e++) {
      const v = Math.pow(10, e);
      if (v >= min * 0.999 && v <= max * 1.001) ticks.push(v);
    }
    return ticks.length > 0 ? ticks : [min, max];
  }

  /** Simple categorical bar chart (e.g. HTS control means). */
  private renderBar(
    labels: string[],
    values: number[],
    title: string,
    yName: string
  ): TemplateResult {
    const W = 480;
    const H = 220;
    const padL = 48;
    const padR = 14;
    const padT = 12;
    const padB = 40;
    const innerW = W - padL - padR;
    const innerH = H - padT - padB;
    const maxV = Math.max(...values, 0);
    const minV = Math.min(...values, 0);
    const yHi = maxV * 1.05 || 1;
    const yLo = minV < 0 ? minV * 1.05 : 0;
    const sy = (v: number) =>
      padT + innerH - ((v - yLo) / (yHi - yLo)) * innerH;
    const n = Math.max(labels.length, 1);
    const slot = innerW / n;
    const bw = slot * 0.6;
    const yTicks = makeTicks(yLo, yHi, 5);
    const svgId = `bar-${title}-${n}`.replace(/[^a-z0-9-]/gi, '_');
    return html`<div class="plot-card">
      <div class="plot-header">
        <span class="plot-title">${title}</span>
        <span class="plot-actions">
          <button
            @click=${() => this.downloadPlotSvg(svgId, title)}
            title="Download as SVG"
          >
            ⇩ SVG
          </button>
          <button
            @click=${() => this.downloadPlotPng(svgId, title)}
            title="Download as PNG"
          >
            ⇩ PNG
          </button>
        </span>
      </div>
      <div class="plot-svg-wrap">
        <svg
          id=${svgId}
          viewBox="0 0 ${W} ${H}"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label=${title}
        >
          ${yTicks.map(
            t => svg`
              <line class="plot-tick-line" x1=${padL} x2=${W - padR} y1=${sy(t)} y2=${sy(t)} />
              <text class="plot-tick" x=${padL - 4} y=${sy(t) + 3} text-anchor="end">${fmtTick(t)}</text>
            `
          )}
          <path
            class="plot-axis-line"
            d=${`M ${padL} ${padT} L ${padL} ${H - padB} L ${W - padR} ${H - padB}`}
          />
          ${labels.map((lab, i) => {
            const cx = padL + slot * i + slot / 2;
            const v = values[i] ?? 0;
            const y0 = sy(0);
            const y1 = sy(v);
            return svg`
              <rect x=${cx - bw / 2} y=${Math.min(y0, y1)} width=${bw} height=${Math.abs(y1 - y0)} fill="#3b6fb6"><title>${lab}: ${v}</title></rect>
              <text class="plot-tick" x=${cx} y=${H - padB + 14} text-anchor="middle">${lab}</text>
            `;
          })}
          <text
            class="plot-axis-label"
            x=${12}
            y=${padT + innerH / 2}
            text-anchor="middle"
            transform=${`rotate(-90, 12, ${padT + innerH / 2})`}
          >
            ${yName}
          </text>
        </svg>
      </div>
    </div>`;
  }

  private renderScatter(
    rows: Array<Record<string, unknown>>,
    xName: string,
    yName: string,
    fit?: { x: number[]; y: number[] } | null,
    logX?: boolean,
    opts?: {
      titleOverride?: string;
      lineMode?: 'step' | 'line';
      colors?: string[];
      pointTitles?: string[];
      hLine?: number;
      vLines?: number[];
      hideDots?: boolean;
      /** 1-based row number per point; when present, points are clickable to
       * exclude that row and refit. */
      rowIndices?: number[];
    }
  ): TemplateResult {
    type Pt = {
      x: number;
      y: number;
      color?: string;
      pointTitle?: string;
      rowIndex?: number;
    };
    const pts: Pt[] = [];
    rows.forEach((r, i) => {
      const x = Number(r[xName]);
      const y = Number(r[yName]);
      if (Number.isFinite(x) && Number.isFinite(y))
        pts.push({
          x,
          y,
          color: opts?.colors?.[i],
          pointTitle: opts?.pointTitles?.[i],
          rowIndex: opts?.rowIndices?.[i],
        });
    });
    const title = opts?.titleOverride ?? `${xName} vs ${yName}`;
    if (pts.length < 2) {
      return html`<div class="plot-card">
        <div class="plot-header">
          <span class="plot-title">${title}</span>
        </div>
        <div class="plot-empty-note">
          Not enough numeric values in the preview to plot.
        </div>
      </div>`;
    }

    const W = 480;
    const H = 220;
    const padL = 48;
    const padR = 14;
    const padT = 12;
    const padB = 32;
    const innerW = W - padL - padR;
    const innerH = H - padT - padB;

    const xs = pts.map(p => p.x);
    const ys = pts.map(p => p.y);
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    const yMin = Math.min(...ys);
    const yMax = Math.max(...ys);
    const yRange = yMax === yMin ? 1 : yMax - yMin;
    const yLo = yMin - yRange * 0.05;
    const yHi = yMax + yRange * 0.05;
    const sy = (y: number) =>
      padT + innerH - ((y - yLo) / (yHi - yLo)) * innerH;

    // Log-x scaling for wide-dynamic-range axes (e.g. dose-response, where
    // doses span many orders of magnitude and a linear axis crushes the
    // low-dose points against the origin). Only valid for strictly positive x.
    const useLog = !!logX && xMin > 0;
    const lxLo = useLog ? Math.log10(xMin) : 0;
    const lxHi = useLog ? Math.log10(xMax) : 0;
    const lxRange = useLog ? (lxHi === lxLo ? 1 : lxHi - lxLo) : 1;
    const xRange = xMax === xMin ? 1 : xMax - xMin;
    const xLo = xMin - xRange * 0.05;
    const xHi = xMax + xRange * 0.05;
    const sx = (x: number) =>
      useLog
        ? padL +
          ((Math.log10(Math.max(x, Number.MIN_VALUE)) - lxLo) / lxRange) *
            innerW
        : padL + ((x - xLo) / (xHi - xLo)) * innerW;

    // Optional fitted-curve overlay (e.g. the 4PL dose-response fit) drawn
    // as a polyline through the model's predicted points.
    let fitPath = '';
    if (fit && fit.x.length > 1) {
      const fpts = fit.x
        .map((xv, i) => ({ x: xv, y: fit.y[i] }))
        .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y))
        .sort((a, b) => a.x - b.x);
      if (fpts.length > 1) {
        fitPath = 'M ' + fpts.map(p => `${sx(p.x)} ${sy(p.y)}`).join(' L ');
      }
    }

    // Optional connecting path through the points: a straight line, or a
    // descending step (e.g. a Kaplan-Meier survival curve).
    let connectPath = '';
    if (opts?.lineMode) {
      const sorted = [...pts].sort((a, b) => a.x - b.x);
      if (sorted.length > 1) {
        const segs = [`M ${sx(sorted[0].x)} ${sy(sorted[0].y)}`];
        for (let i = 1; i < sorted.length; i++) {
          if (opts.lineMode === 'step')
            segs.push(`L ${sx(sorted[i].x)} ${sy(sorted[i - 1].y)}`);
          segs.push(`L ${sx(sorted[i].x)} ${sy(sorted[i].y)}`);
        }
        connectPath = segs.join(' ');
      }
    }

    const xTicks = useLog
      ? this.makeLogTicks(xMin, xMax)
      : makeTicks(xLo, xHi, 5);
    const yTicks = makeTicks(yLo, yHi, 5);

    const svgId = `plot-${xName}-${yName}-${pts.length}`.replace(
      /[^a-z0-9-]/gi,
      '_'
    );

    return html`<div class="plot-card">
      <div class="plot-header">
        <span class="plot-title">${title}</span>
        <span class="plot-actions">
          <button
            @click=${() => this.downloadPlotSvg(svgId, title)}
            title="Download as SVG"
          >
            ⇩ SVG
          </button>
          <button
            @click=${() => this.downloadPlotPng(svgId, title)}
            title="Download as PNG"
          >
            ⇩ PNG
          </button>
        </span>
      </div>
      <div class="plot-svg-wrap">
        <svg
          id=${svgId}
          viewBox="0 0 ${W} ${H}"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label=${`Scatter of ${xName} vs ${yName}, ${pts.length} points`}
        >
          ${yTicks.map(
            t => svg`
              <line
                class="plot-tick-line"
                x1=${padL}
                x2=${W - padR}
                y1=${sy(t)}
                y2=${sy(t)}
              />
              <text
                class="plot-tick"
                x=${padL - 4}
                y=${sy(t) + 3}
                text-anchor="end"
              >${fmtTick(t)}</text>
            `
          )}
          ${xTicks.map(
            t => svg`
              <text
                class="plot-tick"
                x=${sx(t)}
                y=${H - padB + 14}
                text-anchor="middle"
              >${fmtTick(t)}</text>
            `
          )}
          <path
            class="plot-axis-line"
            d=${`M ${padL} ${padT} L ${padL} ${H - padB} L ${W - padR} ${H - padB}`}
          />
          ${(opts?.vLines ?? []).map(
            vx =>
              svg`<line x1=${sx(vx)} x2=${sx(vx)} y1=${padT} y2=${H - padB} stroke="#c4c4c4" stroke-dasharray="4 3" />`
          )}
          ${opts?.hLine !== undefined
            ? svg`<line x1=${padL} x2=${W - padR} y1=${sy(opts.hLine)} y2=${sy(opts.hLine)} stroke="#c4c4c4" stroke-dasharray="4 3" />`
            : nothing}
          ${connectPath
            ? svg`<path d=${connectPath} fill="none" stroke="#002B57" stroke-width="2" />`
            : nothing}
          ${opts?.hideDots
            ? nothing
            : pts.map(p => {
                const clickable = p.rowIndex !== undefined;
                const title = clickable
                  ? `${p.pointTitle ?? `${p.x}, ${p.y}`} — click to exclude row ${p.rowIndex} and refit`
                  : (p.pointTitle ?? `${p.x}, ${p.y}`);
                const style =
                  (p.color ? `fill:${p.color};` : '') +
                  (clickable ? 'cursor:pointer' : '');
                return svg`<circle
                  class="plot-point"
                  cx=${sx(p.x)}
                  cy=${sy(p.y)}
                  r=${clickable ? 4 : 3}
                  style=${style}
                  @click=${
                    clickable
                      ? () => this.excludePointAndRefit(p.rowIndex as number)
                      : nothing
                  }
                ><title>${title}</title></circle>`;
              })}
          ${fitPath
            ? svg`<path d=${fitPath} fill="none" stroke="#002B57" stroke-width="2" />`
            : nothing}
          <text
            class="plot-axis-label"
            x=${padL + innerW / 2}
            y=${H - 4}
            text-anchor="middle"
          >
            ${xName}
          </text>
          <text
            class="plot-axis-label"
            x=${12}
            y=${padT + innerH / 2}
            text-anchor="middle"
            transform=${`rotate(-90, 12, ${padT + innerH / 2})`}
          >
            ${yName}
          </text>
        </svg>
      </div>
    </div>`;
  }

  /**
   * Resolve the live SVG node for a plot by id. BlockSuite components
   * render to light DOM (see `connectedCallback`), so `querySelector`
   * against the component root works without piercing a shadow root.
   */
  private getPlotSvgElement(svgId: string): SVGSVGElement | null {
    const node = this.querySelector(`#${cssEscape(svgId)}`);
    return node instanceof SVGSVGElement ? node : null;
  }

  private downloadPlotSvg = (svgId: string, title: string): void => {
    const svgNode = this.getPlotSvgElement(svgId);
    if (!svgNode) {
      this.showToast('Plot not ready');
      return;
    }
    const serialized = new XMLSerializer().serializeToString(svgNode);
    const blob = new Blob(
      [`<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`],
      { type: 'image/svg+xml' }
    );
    triggerDownload(blob, `${sanitizeFilename(title)}.svg`);
    this.showToast('SVG downloaded');
  };

  private downloadPlotPng = async (
    svgId: string,
    title: string
  ): Promise<void> => {
    const svgNode = this.getPlotSvgElement(svgId);
    if (!svgNode) {
      this.showToast('Plot not ready');
      return;
    }
    try {
      const png = await svgToPng(svgNode, 2);
      triggerDownload(png, `${sanitizeFilename(title)}.png`);
      this.showToast('PNG downloaded');
    } catch (err) {
      console.error('[analyze-result] PNG export failed:', err);
      this.showToast('PNG export failed');
    }
  };

  /**
   * Profile-only metadata table — used when no structured analyze
   * result is available (e.g. custom-analysis path on an HTS plate
   * dataset). Same layout as `renderMeta` but reads only what's on
   * the dataset profile.
   */
  private renderProfileMeta(
    profile: DatasetProfileLoose | null
  ): TemplateResult {
    if (!profile) return html`${nothing}`;
    const items: Array<[string, string]> = [];
    if (profile.name) items.push(['Dataset', profile.name]);
    if (profile.rowCount !== undefined && profile.columnCount !== undefined) {
      items.push(['Shape', `${profile.rowCount} × ${profile.columnCount}`]);
    }
    if (items.length === 0) return html`${nothing}`;
    return html`<div class="meta">
      ${items.map(
        ([k, v]) =>
          html`<span class="meta-item"
            ><span class="label">${k}</span
            ><span class="value">${v}</span></span
          >`
      )}
    </div>`;
  }
}

// Match the backend CSV parser's ColumnType strings as well as common
// pandas/SQL-style dtype labels so the plot renderer works regardless
// of which producer wrote the profile.
const NUMERIC_DTYPE_RE =
  /^(int|integer|float|number|numeric|double|long|short|byte|decimal)/i;
const BOOL_DTYPE_RE = /^(bool|boolean)/i;
const DATE_DTYPE_RE = /(date|time|timestamp)/i;

function isNumericDtype(d: string): boolean {
  return NUMERIC_DTYPE_RE.test(d);
}

function isBoolDtype(d: string): boolean {
  return BOOL_DTYPE_RE.test(d);
}

function isDateDtype(d: string): boolean {
  return DATE_DTYPE_RE.test(d);
}

/** Shorten common dtype names so the header pill stays compact. */
function shortDtype(d: string): string {
  if (/^numeric|^float|^double|^number|^decimal/i.test(d)) return 'num';
  if (/^int|^long|^short|^byte/i.test(d)) return 'int';
  if (BOOL_DTYPE_RE.test(d)) return 'bool';
  if (DATE_DTYPE_RE.test(d)) return 'date';
  if (/^str|^string|^text|^object|^categorical/i.test(d)) return 'str';
  return d.length > 8 ? `${d.slice(0, 8)}…` : d;
}

/**
 * Format a number for the stats chip. Uses 3 significant figures for
 * non-integer values so the chip doesn't grow to 12 characters for a
 * single mean, and integers stay integers.
 */
function fmtNum(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (Number.isInteger(n) && Math.abs(n) < 1e6) return n.toString();
  if (Math.abs(n) >= 1000) return n.toFixed(0);
  if (Math.abs(n) >= 1) return n.toFixed(2);
  if (n === 0) return '0';
  // Use exponential for very small magnitudes; otherwise 3 sig figs.
  if (Math.abs(n) < 1e-3) return n.toExponential(2);
  return n.toPrecision(3);
}

/**
 * Render a cell value in the preview table. Numbers go through fmtNum
 * for readability; booleans get the Unicode ✓/✗; everything else
 * stringifies, with a cap so long strings don't blow up the column.
 */
function formatCell(v: unknown): string {
  if (typeof v === 'number') return fmtNum(v);
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  if (v instanceof Date) return v.toISOString().slice(0, 19).replace('T', ' ');
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
}

/**
 * Produce ~`target` tick values evenly spaced inside [lo, hi] using a
 * "nice number" step (1·10^k / 2·10^k / 5·10^k). Mirrors the d3-scale
 * ticks algorithm without the dependency.
 */
function makeTicks(lo: number, hi: number, target: number): number[] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo >= hi) {
    return Number.isFinite(lo) ? [lo] : [];
  }
  const range = hi - lo;
  const roughStep = range / Math.max(1, target);
  const pow10 = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / pow10;
  let step = pow10;
  if (normalized >= 5) step = pow10 * 5;
  else if (normalized >= 2) step = pow10 * 2;
  const start = Math.ceil(lo / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= hi + 1e-9; v += step) {
    ticks.push(Number(v.toFixed(10)));
    if (ticks.length > 20) break;
  }
  return ticks;
}

/** Compact tick label — integers stay integers, decimals trimmed. */
function fmtTick(n: number): string {
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n) && Math.abs(n) < 1e6) return String(n);
  if (Math.abs(n) >= 1000 || Math.abs(n) < 0.001) return n.toExponential(1);
  return Number(n.toFixed(3)).toString();
}

/**
 * CSS.escape polyfill — used to build safe `#id` selectors when the
 * id contains chars that querySelector would otherwise misinterpret.
 * Modern Chromium/Safari ship CSS.escape, but the test runner / older
 * Electron builds might not, so the fallback handles ASCII-letter +
 * digit + dash + underscore (our id alphabet) defensively.
 */
function cssEscape(id: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(id);
  }
  return id.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-zA-Z0-9-]+/g, '_').replace(/^_+|_+$/g, '') || 'plot';
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Rasterise an SVG node into a PNG Blob at `scale`× resolution.
 * Uses the standard SVG → data-URL → image → canvas pipeline so no
 * server-side rendering is required — keeps the bundle dependency-free.
 */
async function svgToPng(svgNode: SVGSVGElement, scale: number): Promise<Blob> {
  const serialized = new XMLSerializer().serializeToString(svgNode);
  const svgBlob = new Blob(
    [`<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`],
    { type: 'image/svg+xml;charset=utf-8' }
  );
  const url = URL.createObjectURL(svgBlob);
  try {
    const img = await loadImage(url);
    const bbox = svgNode.viewBox?.baseVal;
    const width = bbox && bbox.width ? bbox.width : svgNode.clientWidth || 480;
    const height =
      bbox && bbox.height ? bbox.height : svgNode.clientHeight || 220;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        b => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
        'image/png'
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = err => reject(err);
    img.src = src;
  });
}
